/**
 * Phase 140B.2 Sprint 4 — Highlight Library
 * -----------------------------------------------------------------
 * Route: /streamx/highlights · OWNER + SUPER_ADMIN only.
 *
 * The owner-facing gallery for every MP4 clip extracted from a
 * `livex_highlights` row by the Sprint-3 ffmpeg worker. Each card
 * surfaces a real <video> poster (Emergent-proxied MP4) with hover
 * Play / Download / Delete CTAs.
 *
 * Reads:
 *   GET  /api/streamx/clips?limit=200          (auto-scoped to caller's academy)
 *   DELETE /api/streamx/clips/{id}             (soft-delete)
 *
 * Doctrine: matches the cinematic LivexReplays.jsx visual language —
 * obsidian background, glass cards, white text, generous spacing.
 * NO emoji icons (lucide-react only). Status badges + trigger chips
 * are colour-keyed so a coach can scan a full library at a glance.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  ArrowLeft, Sparkles, Loader2, AlertTriangle, Trash2, Download, Play,
  Film, Clock, Filter, RefreshCw, X, Goal, TrendingUp, Users, Star,
} from 'lucide-react';

// ---------------------------- helpers ----------------------------

function fmtDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return ''; }
}

const TRIGGER_META = {
  manual:          { label: 'Manual', Icon: Star,       color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)' },
  score_change:    { label: 'Goal',   Icon: Goal,       color: '#34d399', bg: 'rgba(52,211,153,0.18)', border: 'rgba(52,211,153,0.45)' },
  reaction_spike:  { label: 'Crowd',  Icon: TrendingUp, color: '#fb923c', bg: 'rgba(251,146,60,0.18)', border: 'rgba(251,146,60,0.45)' },
  viewer_spike:    { label: 'Viewers', Icon: Users,     color: '#a78bfa', bg: 'rgba(167,139,250,0.18)', border: 'rgba(167,139,250,0.45)' },
};
const triggerMeta = (t) => TRIGGER_META[t] || { label: t || 'Moment', Icon: Sparkles, color: '#94A3B8', bg: 'rgba(148,163,184,0.18)', border: 'rgba(148,163,184,0.45)' };

const STATUS_META = {
  ready:      { label: 'Ready',      color: '#6ee7b7', bg: 'rgba(16,185,129,0.18)', border: 'rgba(16,185,129,0.45)' },
  processing: { label: 'Processing', color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)' },
  draft:      { label: 'Queued',     color: '#fbbf24', bg: 'rgba(251,191,36,0.18)', border: 'rgba(251,191,36,0.45)' },
  failed:     { label: 'Failed',     color: '#fda4af', bg: 'rgba(244,63,94,0.18)', border: 'rgba(244,63,94,0.45)' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || 'Unknown', color: '#94A3B8', bg: 'rgba(148,163,184,0.18)', border: 'rgba(148,163,184,0.45)' };

// ---------------------------- atoms ------------------------------

function Anim({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ShimmerCard() {
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="aspect-video relative">
        <motion.div
          className="absolute inset-0"
          animate={{ x: ['-100%', '100%'] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'linear' }}
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)' }}
        />
      </div>
      <div className="p-4 space-y-2">
        <div className="h-3 rounded bg-white/[0.04]" />
        <div className="h-3 rounded bg-white/[0.04] w-2/3" />
      </div>
    </div>
  );
}

// ---------------------------- clip card --------------------------

function ClipCard({ clip, onDelete, busyId, onPlay }) {
  const tMeta = triggerMeta(clip.trigger_type);
  const sMeta = statusMeta(clip.status);
  const TIcon = tMeta.Icon;
  const isReady = clip.status === 'ready' && clip.mp4_url;
  const isFailed = clip.status === 'failed';
  const isProcessing = clip.status === 'processing' || clip.status === 'draft';
  const busy = busyId === clip.id;

  return (
    <div
      data-testid={`streamx-clip-card-${clip.id}`}
      className="group relative flex flex-col rounded-2xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:shadow-[0_24px_64px_rgba(255,59,48,0.14)]"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(24px)',
      }}
    >
      {/* Thumbnail / video */}
      <button
        type="button"
        onClick={() => isReady && onPlay && onPlay(clip)}
        disabled={!isReady}
        data-testid={`streamx-clip-card-${clip.id}-thumb`}
        className="relative aspect-video overflow-hidden text-left disabled:cursor-default"
        style={{
          background: `radial-gradient(50% 50% at 45% 45%, ${tMeta.color}22, #0A0A0A 75%), linear-gradient(135deg, #14080A 0%, #0A0A0A 100%)`,
        }}
      >
        {isReady && clip.thumbnail_url && (
          <img
            src={clip.thumbnail_url}
            alt={clip.title || 'Clip thumbnail'}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        )}
        {/* Centered play overlay only for ready clips */}
        {isReady && (
          <div className="absolute inset-0 grid place-items-center">
            <motion.div
              animate={{ scale: [1, 1.04, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              className="h-14 w-14 rounded-full grid place-items-center transition-transform duration-700 group-hover:scale-110"
              style={{
                background: 'linear-gradient(135deg, rgba(255,59,48,0.92), rgba(153,0,0,0.92))',
                boxShadow: '0 14px 36px rgba(255,59,48,0.32)',
              }}
            >
              <Play className="h-5 w-5 text-white ml-0.5" fill="currentColor" />
            </motion.div>
          </div>
        )}
        {/* Processing spinner overlay */}
        {isProcessing && (
          <div className="absolute inset-0 grid place-items-center bg-black/40">
            <Loader2 className="h-8 w-8 text-amber-300 animate-spin" />
          </div>
        )}
        {/* Failed overlay */}
        {isFailed && (
          <div className="absolute inset-0 grid place-items-center bg-black/60">
            <div className="text-center px-4">
              <AlertTriangle className="h-7 w-7 text-rose-300 mx-auto mb-1" />
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-rose-200">Clip failed</div>
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/80 to-transparent" />
        {/* Status pill */}
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
          style={{ background: sMeta.bg, color: sMeta.color, border: `1px solid ${sMeta.border}` }}
          data-testid={`streamx-clip-card-${clip.id}-status`}
        >
          {sMeta.label}
        </span>
        {/* Duration pill */}
        <div
          className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums"
          style={{
            background: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.10)',
            color: 'white',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Clock className="h-2.5 w-2.5" /> {fmtDuration(clip.duration_seconds)}
        </div>
      </button>

      {/* Body */}
      <div className="flex-1 p-4 sm:p-5 flex flex-col">
        <h3 className="text-base font-semibold tracking-tight text-white leading-snug line-clamp-2 group-hover:text-[#FF6B5C] transition-colors">
          {clip.title || 'Untitled Highlight'}
        </h3>
        {clip.stream_title && (
          <div className="mt-1 text-[11px] font-medium text-white/55 line-clamp-1" data-testid={`streamx-clip-card-${clip.id}-stream`}>
            From · {clip.stream_title}
          </div>
        )}
        <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ background: tMeta.bg, color: tMeta.color, border: `1px solid ${tMeta.border}` }}
            data-testid={`streamx-clip-card-${clip.id}-trigger`}
          >
            <TIcon className="h-2.5 w-2.5" /> {tMeta.label}
          </span>
          {clip.extract_mode === 'auto' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: 'rgba(255,255,255,0.04)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.12)' }}>
              Auto
            </span>
          )}
        </div>
        <div className="mt-3 text-[11px] font-medium text-white/45 tabular-nums">
          {fmtDate(clip.created_at)}
        </div>

        <div className="mt-4 flex items-center gap-1.5 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {isReady ? (
            <>
              <button
                type="button"
                onClick={() => onPlay && onPlay(clip)}
                data-testid={`streamx-clip-card-${clip.id}-play`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-emerald-200 hover:text-emerald-100 transition-all duration-500"
                style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)' }}
              >
                <Play className="h-3 w-3 fill-current" /> Play
              </button>
              <a
                href={clip.mp4_url}
                download
                data-testid={`streamx-clip-card-${clip.id}-download`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-white/70 hover:text-white transition-all duration-500"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)' }}
                title="Download MP4"
              >
                <Download className="h-3 w-3" />
              </a>
              <button
                type="button"
                onClick={() => onDelete && onDelete(clip)}
                disabled={busy}
                data-testid={`streamx-clip-card-${clip.id}-delete`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold text-rose-300 hover:text-rose-200 transition-all duration-500 disabled:opacity-50"
                style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                title="Soft-delete clip"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              </button>
            </>
          ) : isFailed ? (
            <div
              className="flex-1 text-center text-[11px] font-semibold text-rose-200/70 uppercase tracking-wider py-2"
              data-testid={`streamx-clip-card-${clip.id}-failed-note`}
              title={clip.status_note || 'Clip extraction failed'}
            >
              {clip.status_note ? clip.status_note.slice(0, 48) : 'Extraction failed'}
            </div>
          ) : (
            <div className="flex-1 text-center text-[11px] font-semibold text-amber-200/70 uppercase tracking-wider py-2">
              <Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Generating clip…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------- player modal -----------------------

function ClipPlayerModal({ clip, onClose }) {
  if (!clip) return null;
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-6 bg-black/85"
      onClick={onClose}
      data-testid="streamx-clip-player-modal"
    >
      <div
        className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0A0A0A',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close clip player"
          data-testid="streamx-clip-player-close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full grid place-items-center bg-black/60 border border-white/20 text-white hover:bg-black"
        >
          <X className="h-4 w-4" />
        </button>
        <video
          src={clip.mp4_url}
          poster={clip.thumbnail_url || ''}
          controls
          autoPlay
          playsInline
          className="w-full aspect-video bg-black"
          data-testid="streamx-clip-player-video"
        />
        <div className="px-5 py-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <h3 className="text-base font-bold text-white truncate">{clip.title || 'Untitled Highlight'}</h3>
          {clip.stream_title && (
            <div className="mt-1 text-[12px] font-medium text-white/55">From · {clip.stream_title}</div>
          )}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <a
              href={clip.mp4_url}
              download
              data-testid="streamx-clip-player-download"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white border border-white/20 hover:border-[#FF5A1F]"
            >
              <Download className="h-3 w-3" /> Download MP4
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------- page -------------------------------

export default function LivexHighlights() {
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTrigger, setFilterTrigger] = useState('all');
  const [filterStream, setFilterStream] = useState('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState('');
  const [activeClip, setActiveClip] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get('/streamx/clips', { params: { limit: 200 } });
      setClips(res.data?.entries || []);
      setError('');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not load highlight library.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // Initial fetch — inlined inside the effect (not via load()) so the
  // React 19 `react-hooks/set-state-in-effect` lint rule is satisfied.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get('/streamx/clips', { params: { limit: 200 } });
        if (!active) return;
        setClips(res.data?.entries || []);
        setError('');
      } catch (e) {
        if (!active) return;
        setError(e?.response?.data?.detail || 'Could not load highlight library.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Auto-refresh while there are any processing/draft clips. The interval
  // mounts exactly once; the actual fetch is inlined so we don't trip the
  // `react-hooks/set-state-in-effect` rule. We read the current snapshot
  // via a ref so updating `clips` doesn't reset the timer.
  const clipsRef = useRef(clips);
  useEffect(() => { clipsRef.current = clips; }, [clips]);
  useEffect(() => {
    const tick = setInterval(async () => {
      const cur = clipsRef.current || [];
      const hasInFlight = cur.some((c) => c.status === 'processing' || c.status === 'draft');
      if (!hasInFlight) return;
      try {
        const res = await api.get('/streamx/clips', { params: { limit: 200 } });
        setClips(res.data?.entries || []);
      } catch (_e) { /* silent poll failure — next tick retries */ }
    }, 20000);
    return () => clearInterval(tick);
  }, []);

  async function handleDelete(clip) {
    if (!clip?.id) return;
    if (!window.confirm(`Delete "${clip.title || 'this clip'}"? This hides it from your library — the MP4 itself stays in storage.`)) {
      return;
    }
    setBusyId(clip.id);
    try {
      await api.delete(`/streamx/clips/${clip.id}`);
      setClips((prev) => prev.filter((c) => c.id !== clip.id));
      toast.success('Clip removed from library.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not delete clip.';
      toast.error(msg);
    } finally {
      setBusyId('');
    }
  }

  // Build the stream filter options client-side (the list endpoint already
  // enriches with `stream_title`, so we don't need a second round-trip).
  const streamOptions = useMemo(() => {
    const map = new Map();
    for (const c of clips) {
      const sid = c.stream_id || '';
      if (!sid) continue;
      if (!map.has(sid)) {
        map.set(sid, { id: sid, title: c.stream_title || 'Untitled stream', count: 0 });
      }
      map.get(sid).count += 1;
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [clips]);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    return clips.filter((c) => {
      if (filterStatus !== 'all' && c.status !== filterStatus) return false;
      if (filterTrigger !== 'all' && c.trigger_type !== filterTrigger) return false;
      if (filterStream !== 'all' && c.stream_id !== filterStream) return false;
      if (!q) return true;
      const hay = `${c.title || ''} ${c.stream_title || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [clips, filterStatus, filterTrigger, filterStream, search]);

  // Counts by status — drives the headline KPI strip.
  const counts = useMemo(() => {
    const out = { total: clips.length, ready: 0, processing: 0, failed: 0 };
    for (const c of clips) {
      if (c.status === 'ready') out.ready += 1;
      else if (c.status === 'processing' || c.status === 'draft') out.processing += 1;
      else if (c.status === 'failed') out.failed += 1;
    }
    return out;
  }, [clips]);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0A0A0A' }} data-testid="streamx-highlights-page">
      {/* HERO */}
      <header className="relative overflow-hidden border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(80% 60% at 10% 0%, rgba(255,90,31,0.16), transparent 60%), radial-gradient(60% 50% at 90% 100%, rgba(220,38,38,0.18), transparent 60%), #07111F',
        }} />
        <div className="absolute inset-0 opacity-20" style={{
          background: 'repeating-linear-gradient(45deg, transparent, transparent 14px, rgba(255,255,255,0.02) 14px, rgba(255,255,255,0.02) 16px)',
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <button
              onClick={() => navigate('/streamx/dashboard')}
              data-testid="streamx-highlights-back"
              className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Cockpit
            </button>
            <div className="text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · HIGHLIGHT LIBRARY</div>
          </div>
          <Anim>
            <div className="flex items-start gap-4 sm:gap-6 flex-wrap">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl grid place-items-center"
                style={{ background: 'linear-gradient(135deg, rgba(255,59,48,0.22), rgba(153,0,0,0.22))', border: '1px solid rgba(255,90,31,0.45)' }}>
                <Film className="h-7 w-7 text-[#FF5A1F]" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight" data-testid="streamx-highlights-title">
                  Highlight Library
                </h1>
                <p className="mt-2 sm:mt-3 text-sm sm:text-base text-white/65 max-w-2xl">
                  Every signature moment from your live broadcasts — auto-extracted, parent-ready, downloadable.
                </p>
              </div>
            </div>
          </Anim>

          <Anim delay={0.05}>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KpiTile label="Total"      value={counts.total}      testid="streamx-highlights-kpi-total" />
              <KpiTile label="Ready"      value={counts.ready}      accent="#34d399" testid="streamx-highlights-kpi-ready" />
              <KpiTile label="Generating" value={counts.processing} accent="#fbbf24" testid="streamx-highlights-kpi-processing" />
              <KpiTile label="Failed"     value={counts.failed}     accent="#fda4af" testid="streamx-highlights-kpi-failed" />
            </div>
          </Anim>
        </div>
      </header>

      {/* FILTER STRIP */}
      <section className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'rgba(7,17,31,0.4)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.1em] text-white/55">
            <Filter className="h-3.5 w-3.5" /> Filter
          </div>

          {/* Status chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { v: 'all',        label: 'All' },
              { v: 'ready',      label: 'Ready' },
              { v: 'processing', label: 'Generating' },
              { v: 'failed',     label: 'Failed' },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setFilterStatus(opt.v)}
                data-testid={`streamx-highlights-filter-status-${opt.v}`}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md border-2 transition-all ${
                  filterStatus === opt.v
                    ? 'bg-[#FF5A1F] border-[#FF5A1F] text-white'
                    : 'border-white/10 text-white/70 hover:border-[#FF5A1F]'
                }`}
                style={filterStatus === opt.v ? {} : { background: 'rgba(255,255,255,0.02)' }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Trigger select */}
          <select
            value={filterTrigger}
            onChange={(e) => setFilterTrigger(e.target.value)}
            data-testid="streamx-highlights-filter-trigger"
            className="bg-[#0c1a2e] border-2 border-white/10 text-white text-[11px] font-bold uppercase tracking-wider rounded-md px-2.5 py-1.5 hover:border-[#FF5A1F] transition-colors"
          >
            <option value="all">All Triggers</option>
            <option value="manual">Manual ⭐</option>
            <option value="score_change">Goals / Scores</option>
            <option value="reaction_spike">Crowd Spikes</option>
            <option value="viewer_spike">Viewer Surges</option>
          </select>

          {/* Stream select */}
          <select
            value={filterStream}
            onChange={(e) => setFilterStream(e.target.value)}
            data-testid="streamx-highlights-filter-stream"
            className="bg-[#0c1a2e] border-2 border-white/10 text-white text-[11px] font-bold uppercase tracking-wider rounded-md px-2.5 py-1.5 hover:border-[#FF5A1F] transition-colors max-w-[220px] truncate"
          >
            <option value="all">All Streams</option>
            {streamOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.title.slice(0, 38)} · {s.count}</option>
            ))}
          </select>

          {/* Search */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search clips…"
            data-testid="streamx-highlights-search"
            className="flex-1 min-w-[160px] bg-[#0c1a2e] border-2 border-white/10 text-white text-[12px] font-medium rounded-md px-3 py-1.5 placeholder-white/35 hover:border-[#FF5A1F] focus:border-[#FF5A1F] focus:outline-none transition-colors"
          />

          {/* Refresh */}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="streamx-highlights-refresh"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md border-2 border-white/10 text-white/70 hover:border-[#FF5A1F] hover:text-white transition-all disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </section>

      {/* MAIN GRID */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="streamx-highlights-shimmer">
            {Array.from({ length: 6 }).map((_, i) => <ShimmerCard key={i} />)}
          </div>
        ) : error ? (
          <div className="py-20 text-center" data-testid="streamx-highlights-error">
            <AlertTriangle className="h-12 w-12 mx-auto text-rose-300 mb-3" />
            <h2 className="text-2xl font-black text-white mb-2">Could not load highlights</h2>
            <p className="text-sm text-white/55 mb-5 max-w-md mx-auto">{error}</p>
            <button onClick={load}
              data-testid="streamx-highlights-error-retry"
              className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <RefreshCw className="h-4 w-4" /> Try again
            </button>
          </div>
        ) : clips.length === 0 ? (
          <div className="py-20 text-center" data-testid="streamx-highlights-empty">
            <Sparkles className="h-12 w-12 mx-auto text-white/40 mb-3" />
            <h2 className="text-2xl font-black text-white mb-2">No highlights captured yet</h2>
            <p className="text-sm text-white/55 mb-6 max-w-md mx-auto">
              Mark moments live with the ⭐ button while broadcasting, or let StreamX auto-detect goals,
              crowd spikes, and viewer surges. They&apos;ll all appear here.
            </p>
            <Link to="/streamx/studio" data-testid="streamx-highlights-empty-cta"
              className="inline-flex items-center gap-2 px-5 py-3 text-base font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Film className="h-4 w-4" /> Go to Studio
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="streamx-highlights-filter-empty">
            <p className="text-sm text-white/55">No clips match these filters. Try another combination.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5" data-testid="streamx-highlights-grid">
            {filtered.map((c) => (
              <ClipCard key={c.id} clip={c} busyId={busyId} onDelete={handleDelete} onPlay={setActiveClip} />
            ))}
          </div>
        )}
      </main>

      <ClipPlayerModal clip={activeClip} onClose={() => setActiveClip(null)} />
    </div>
  );
}

// ------------- KPI tile (inlined to keep file self-contained) -------------

function KpiTile({ label, value, accent, testid }) {
  return (
    <div
      className="p-4 rounded-xl"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderTopWidth: '3px',
        borderTopColor: accent || '#FF5A1F',
      }}
      data-testid={testid}
    >
      <div className="text-[11px] font-black uppercase tracking-[0.15em] text-white/55">{label}</div>
      <div className="mt-1 text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{value.toLocaleString()}</div>
    </div>
  );
}
